<template>
  <Teleport v-if="visivel" :to="areaDaAplicacao ? '#container-externo' : 'body'">
    <component
      :is="areaDaAplicacao ? 'dialog' : 'div'"
      :open="areaDaAplicacao ? true : undefined"
      class="v--modal-overlay"
      :class="{ 'v--modal-overlay--area-da-aplicacao': areaDaAplicacao }"
      :data-modal="name"
      role="dialog"
      :aria-modal="!areaDaAplicacao"
      @mousedown.self="aoClicarFundo"
      @touchstart.self="aoClicarFundo"
    >
      <div class="v--modal-top-right">
        <slot name="top-right" />
      </div>
      <div
        ref="box"
        class="v--modal-box"
        :class="classes"
        :style="estiloContainer"
        @mousedown="aoClicarMouse"
      >
        <slot />
      </div>
    </component>
  </Teleport>
</template>

<script>
import { modalBus } from './modalPlugin'

export default {
  name: 'VModal',
  emits: ['before-open', 'opened', 'before-close', 'closed', 'close'],

  props: {
    name:      { type: String, required: true },
    width:     { type: [String, Number], default: 600 },
    height:    { type: [String, Number], default: 'auto' },
    minWidth:  { type: Number, default: 0 },
    minHeight: { type: Number, default: 0 },
    maxHeight: { type: Number, default: 0 },
    classes:   { type: [String, Array, Object], default: '' },
    draggable: { type: Boolean, default: false },
    scrollable: { type: Boolean, default: false },
    adaptive:  { type: Boolean, default: false },
    areaDaAplicacao: { type: Boolean, default: false },
    clickToClose: { type: Boolean, default: true }
  },

  data () {
    return {
      visivel: false,
      parametrosAtuais: {},
      deslocamentoArrasto: { x: 0, y: 0 },
      arrastando: false,
      ancorArrasto: { x: 0, y: 0 },
      deslocamentoInicialArrasto: { x: 0, y: 0 }
    }
  },

  computed: {
    estiloContainer () {
      const paraUnidade = val => (typeof val === 'number' ? `${val}px` : val)

      const estilo = {
        width: this.adaptive ? `min(${paraUnidade(this.width)}, 100%)` : paraUnidade(this.width),
        height: paraUnidade(this.height)
      }

      if (this.minWidth)  estilo.minWidth  = `${this.minWidth}px`
      if (this.minHeight) estilo.minHeight = `${this.minHeight}px`
      if (this.maxHeight) estilo.maxHeight = `${this.maxHeight}px`
      if (this.scrollable) estilo.overflowY = 'auto'
      if (this.draggable) {
        estilo.cursor = 'move'
        estilo.transform = `translate(${this.deslocamentoArrasto.x}px, ${this.deslocamentoArrasto.y}px)`
      }

      return estilo
    }
  },

  mounted () {
    this.manipuladorExibir = params => this.exibir(params)
    this.manipuladorOcultar = () => this.ocultar()
    modalBus.on(`modal:show:${this.name}`, this.manipuladorExibir)
    modalBus.on(`modal:hide:${this.name}`, this.manipuladorOcultar)

    if (this.clickToClose) {
      window.addEventListener('keyup', this.aoTeclarEsc)
    }
  },

  beforeUnmount () {
    this.limpar()
  },

  methods: {
    exibir (params = {}) {
      this.parametrosAtuais = params
      this.$emit('before-open', { params })
      this.visivel = true
      this.$nextTick(() => {
        this.$emit('opened', { params })
      })
    },

    ocultar () {
      this.$emit('before-close')
      this.visivel = false
      this.$emit('closed')
      this.$emit('close')
    },

    limpar () {
      modalBus.off(`modal:show:${this.name}`, this.manipuladorExibir)
      modalBus.off(`modal:hide:${this.name}`, this.manipuladorOcultar)
      this.removerListenersArrasto()
      if (this.clickToClose) {
        window.removeEventListener('keyup', this.aoTeclarEsc)
      }
    },

    aoClicarFundo () {
      if (this.clickToClose) {
        this.ocultar()
      }
    },

    aoTeclarEsc (event) {
      if (event.key === 'Escape' && this.visivel) {
        this.ocultar()
      }
    },

    // --- arrasto ---
    aoClicarMouse (e) {
      if (!this.draggable) return
      this.arrastando = true
      this.ancorArrasto = { x: e.clientX, y: e.clientY }
      this.deslocamentoInicialArrasto = { ...this.deslocamentoArrasto }
      document.addEventListener('mousemove', this.aoMoverMouse)
      document.addEventListener('mouseup', this.aoSoltarMouse)
    },

    aoMoverMouse (e) {
      if (!this.arrastando) return
      this.deslocamentoArrasto = {
        x: this.deslocamentoInicialArrasto.x + (e.clientX - this.ancorArrasto.x),
        y: this.deslocamentoInicialArrasto.y + (e.clientY - this.ancorArrasto.y)
      }
    },

    aoSoltarMouse () {
      this.arrastando = false
      this.removerListenersArrasto()
    },

    removerListenersArrasto () {
      document.removeEventListener('mousemove', this.aoMoverMouse)
      document.removeEventListener('mouseup', this.aoSoltarMouse)
    }
  }
}
</script>

<style>
.v--modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 999999;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.2);
}

.v--modal-box {
  position: relative;
  background: #fff;
  border-radius: 4px;
  box-shadow: 0 20px 60px -2px rgba(27, 33, 58, 0.4);
  overflow: hidden;
  box-sizing: border-box;
}

/* O host já reserva esta largura para a conversa. O Teleport para o host
 * mantém a variável reativa ao abrir, recolher e redimensionar o assistente.
 * O dialog não modal também recebe os popovers dos seletores, sem tornar
 * a conversa inerte nem enviar suas opções para trás do overlay no body. */
.v--modal-overlay--area-da-aplicacao {
  right: var(--assistente-largura, 0px);
  width: auto;
  height: auto;
  max-width: none;
  max-height: none;
  margin: 0;
  padding: 0;
  border: 0;
  color: inherit;
  overflow: hidden;
}

.v--modal-overlay--area-da-aplicacao > .v--modal-box {
  max-width: 100%;
  max-height: 100%;
}

.v--modal-top-right {
  display: block;
  position: absolute;
  right: 0;
  top: 0;
}
</style>
